import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Plane, MapPin, Phone, Mail, Clock, ArrowRight, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useSiteSettings } from '../context/SiteSettingsContext';

const socialIcons = {
  facebook: (
    <svg viewBox="0 0 24 24" className="w-[18px] h-[18px] fill-current"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
  ),
  instagram: (
    <svg viewBox="0 0 24 24" className="w-[18px] h-[18px] fill-current"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
  ),
  twitter: (
    <svg viewBox="0 0 24 24" className="w-[18px] h-[18px] fill-current"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
  ),
  youtube: (
    <svg viewBox="0 0 24 24" className="w-[18px] h-[18px] fill-current"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
  ),
  linkedin: (
    <svg viewBox="0 0 24 24" className="w-[18px] h-[18px] fill-current"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
  ),
};

const Footer = () => {
  const { settings } = useSiteSettings();
  const location = useLocation();
  const contactInfo = settings?.contact_info || {};
  const footer = settings?.footer || {};
  
  // Logo from public folder
  const logoUrl = "/sentora-logo.png";
  const socialLinks = settings?.social_links || {};
  const branding = settings?.branding || {};
  
  const companyName = branding.company_name || "Sentora Holidays";
  const tagline = branding.tagline || "a Unit of Sam Tours and Travels Pvt Ltd";

  // Check if on contact page
  const isContactPage = location.pathname === '/contact';

  // Quick links - customizable via footer settings or use defaults
  const quickLinks = footer.quick_links || [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Tour Packages', path: '/packages' },
    { name: 'Fleet', path: '/fleet' },
    { name: 'AI Trip Planner', path: '/ai-planner' },
    { name: 'Contact Us', path: '/contact' },
  ];

  // Popular destinations - customizable via footer settings
  const destinations = footer.popular_destinations || [
    { name: 'Kerala, India', link: '/packages?q=kerala' },
    { name: 'Rajasthan, India', link: '/packages?q=rajasthan' },
    { name: 'Goa, India', link: '/packages?q=goa' },
    { name: 'Kashmir, India', link: '/packages?q=kashmir' },
    { name: 'Tamil Nadu, India', link: '/packages?q=tamil' },
    { name: 'Ladakh, India', link: '/packages?q=ladakh' },
  ];

  return (
    <footer className="bg-s-bg text-s-text2 border-t border-s-border" role="contentinfo">
      {/* Newsletter Section - Only on Contact page */}
      {isContactPage && (
        <div className="bg-gradient-to-r from-s-primary to-s-primary">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-white text-center md:text-left">
                <h3 className="text-xl md:text-2xl font-bold mb-2">Get Exclusive Travel Deals</h3>
                <p className="text-white/70 text-sm md:text-base">Subscribe and save up to 40% on your next trip</p>
              </div>
              <form className="flex flex-col sm:flex-row gap-2 w-full md:w-auto" onSubmit={(e) => { e.preventDefault(); const email = e.target.querySelector('input').value; if(email) window.location.href = `mailto:tours@sentoraholidays.com?subject=Subscribe for Travel Deals&body=Please add me to your travel deals mailing list. My email: ${email}`; }}>
                <Input 
                  type="email" 
                  placeholder="Enter your email" 
                  className="bg-white/20 border-white/30 text-white placeholder:text-white/70 min-w-[200px] md:min-w-[250px] rounded-full h-12"
                  aria-label="Email for newsletter"
                  required
                />
                <Button type="submit" className="bg-white text-s-primary hover:bg-s-bg2 rounded-full px-6 h-12">
                  <Send size={18} className="mr-2" />
                  Subscribe
                </Button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
          {/* Company Info */}
          <div className="col-span-2 md:col-span-2 lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-4">
              <img src={logoUrl} alt="Sentora" className="h-12 w-auto object-contain" />
              <div>
                <p className="text-lg font-bold text-[#59090c] font-heritage">Sentora Holidays</p>
                <p className="text-[9px] text-s-muted">a Unit of Sam Tours and Travels Pvt Ltd</p>
              </div>
            </Link>
            <p className="text-s-text2 mb-6 leading-relaxed text-sm">
              {footer.description || 'Crafting unforgettable travel experiences for 40 years.'}
            </p>
            <address className="space-y-3 not-italic">
              <div className="flex items-start gap-3">
                <span className="text-sm">{contactInfo.address || ''}</span>
              </div>
              <div className="flex items-center gap-3">
                <a href={`tel:${contactInfo.phone || ''}`} className="text-sm hover:text-s-primary transition-colors">
                  {contactInfo.phone || ''}
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Mail size={18} className="text-s-primary" />
                <a href={`mailto:${contactInfo.email || ''}`} className="text-sm hover:text-s-primary transition-colors">
                  {contactInfo.email || ''}
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Clock size={18} className="text-s-primary" />
                <span className="text-sm">{contactInfo.working_hours || ''}</span>
              </div>
            </address>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-s-text text-base md:text-lg font-semibold mb-4 md:mb-6">Quick Links</h4>
            <nav aria-label="Footer quick links">
              <ul className="space-y-2 md:space-y-3">
                {quickLinks.map((link, idx) => (
                  <li key={idx}>
                    <Link 
                      to={link.path}
                      className="flex items-center gap-2 text-s-text2 hover:text-s-primary transition-colors group text-sm"
                    >
                      <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          {/* Popular Destinations */}
          <div>
            <h4 className="text-s-text text-base md:text-lg font-semibold mb-4 md:mb-6">Popular Destinations</h4>
            <nav aria-label="Popular destinations">
              <ul className="space-y-2 md:space-y-3">
                {destinations.map((dest, idx) => (
                  <li key={idx}>
                    <Link 
                      to={dest.link || '/packages'}
                      className="flex items-center gap-2 text-s-text2 hover:text-s-primary transition-colors group text-sm"
                    >
                      <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                      {dest.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          {/* Social & Contact */}
          <div className="col-span-2 md:col-span-1">
            <h4 className="text-s-text text-base md:text-lg font-semibold mb-4 md:mb-6">Connect With Us</h4>
            <div className="flex flex-wrap gap-2 mb-6" data-testid="social-links">
              {Object.entries(socialLinks).filter(([, url]) => url).map(([platform, url]) => (
                <a
                  key={platform}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-s-card flex items-center justify-center hover:bg-s-primary transition-colors"
                  aria-label={platform}
                  data-testid={`social-${platform}`}
                >
                  {socialIcons[platform] || <span className="text-xs capitalize">{platform[0]}</span>}
                </a>
              ))}
              {Object.values(socialLinks).every(v => !v) && (
                <p className="text-s-muted text-sm">Social links coming soon</p>
              )}
            </div>
            <p className="text-s-text2 text-sm">
              <strong className="text-s-text">Need help?</strong><br />
              Call us at <a href={`tel:${contactInfo.phone || ''}`} className="text-s-primary hover:underline">{contactInfo.phone}</a><br />
              or email <a href={`mailto:${contactInfo.email || ''}`} className="text-s-primary hover:underline">{contactInfo.email}</a>
            </p>
          </div>
        </div>
      </div>

      {/* Copyright */}
      <div className="bg-s-bg2 py-4 md:py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-xs md:text-sm text-s-muted">
            <p>&copy; {new Date().getFullYear()} {footer.copyright || 'Sentora Holidays Tours & Travels Pvt. Ltd. All rights reserved. | Est. 1985'}</p>
            <nav className="flex flex-wrap justify-center gap-4 md:gap-6" aria-label="Legal links">
              <Link to="/privacy-policy" className="hover:text-s-primary transition-colors">Privacy Policy</Link>
              <Link to="/terms-conditions" className="hover:text-s-primary transition-colors">Terms & Conditions</Link>
              <Link to="/refund-policy" className="hover:text-s-primary transition-colors">Refund Policy</Link>
            </nav>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
